import { TealiumEventNames, TealiumEventCategory } from './analytics';
import { ActivePodcastOrStation } from '../player';

export enum VolumeEventLabel {
  MUTE = 'mute',
  UNMUTE = 'umute',
  UP = 'up',
  DOWN = 'down',
}

export enum EventPageSection {
  ABOVE_THE_FOLD = 'above-the-fold',
  BELOW_THE_FOLD = 'below-the-fold',
}

// Audio Only
export enum MediaBitrate {
  '64KBPS' = '64kbps',
  '128KBPS' = '128kbps',
  '256KBPS' = '256kbps',
  '320KBPS' = '320kbps',
}

// Video Only
export enum MediaQuality {
  '240P' = '240p',
  '360P' = '360p',
  '480P' = '480p',
  '720P' = '720p',
  '1080P' = '1080p',
}

export enum MediaType {
  VIDEO = 'video',
  AUDIO = 'audio',
}

export enum MediaAudioType {
  PODCAST = 'podcast',
  RADIO_LIVE = 'radio-live',
  ONDEMAND_RADIO = 'ondemand-radio',
  LUISTERBOEK = 'luisterboek',
}

export enum MediaVideoType {
  EPISODE = 'episode',
  CLIP = 'clip',
  LIVE = 'live',
  MOVIE = 'movie',
  SERIES = 'series',
}

// Type created based on the requirements
export enum PageCategory {
  HOME = 'home',
  INDEX = 'index',
  DETAIL = 'detail',
  CART = 'cart',
}

export enum PlayerCasting {
  AIRPLAY = 'airplay',
  CHROMECAST = 'chromecast',
  HEOS = 'heos',
  CARPLAY = 'carplay',
}

export enum PlayerStatus {
  DURATION = 'duration',
  END = 'end',
  MUTE = 'mute',
  PAUSE = 'pause',
  PLAY = 'play',
  RESUME = 'resume',
  START = 'start',
  STOP = 'stop',
  UNMUTE = 'unmute',
  ERROR = 'error',
  IDLE = 'idle',
  DISCONNECT = 'disconnect',
}

export enum PlayerForm {
  NORMAL_PLAYER = 'normal player',
  FLOATING_PLAYER = 'floating player',
}

export interface ITealiumEvent {
  // Event Group
  event_name: TealiumEventNames;
  event_category: TealiumEventCategory;
  event_label?: string | number | null;
  event_value?: string | number | null;
  event_page_section?: EventPageSection | null;

  // Parent Group
  parent_hostname?: string;

  // Media Group
  media_bitrate?: MediaBitrate;
  media_datepublished?: number | null;
  media_duration?: number | null;
  media_genre?: string | null;
  media_id?: string; // station slug | on demand / podcast id
  media_name?: string;
  media_offset?: number | null;
  media_program_episodenumber?: number | null;
  media_program_name?: string;
  media_program_season?: number;
  media_quality?: MediaQuality | null;
  media_type?: MediaType | null;
  media_audiotype?: MediaAudioType;
  media_meta_description?: string | null;
  media_stationname?: string;
  media_thumbnail?: string | null; // URI
  media_size?: number | null; // size in kilobytes
  media_videotype?: MediaVideoType | null;

  // Page Group
  page_category?: PageCategory | null;
  page_host?: string; // Base URL

  // Player Group
  player_autoplay?: boolean;
  player_casting?: boolean | PlayerCasting;
  player_channel?: string | null;
  player_channelid?: string;
  player_embedurl?: string;
  player_fullscreen?: boolean;
  player_width?: number | null;
  player_height?: number | null;
  player_id?: string;
  player_state?: PlayerStatus;
  player_engine?: 'html';
  player_version?: string | null;
  player_form?: PlayerForm;

  // Experiment Group
  experiment_tests?: string;
  experiment_test_variant?: string;
  experiment_features?: string;
}

// Partial dynamic type declarations for event groups
type TealiumEventGroup<T, U extends string> = {
  [K in keyof T as K extends `${U}${string}` ? K : never]: T[K];
};

export const tealiumEventBaseGroupPrefix = 'event_';
export type TealiumEventBaseGroup = TealiumEventGroup<ITealiumEvent, typeof tealiumEventBaseGroupPrefix>;

export const tealiumEventParentGroupPrefix = 'parent_';
export type TealiumEventParentGroup = TealiumEventGroup<ITealiumEvent, typeof tealiumEventParentGroupPrefix>;

export const tealiumEventMediaGroupPrefix = 'media_';
export type TealiumEventMediaGroup = TealiumEventGroup<ITealiumEvent, typeof tealiumEventMediaGroupPrefix>;

export const tealiumEventPageGroupPrefix = 'page_';
export type TealiumEventPageGroup = TealiumEventGroup<ITealiumEvent, typeof tealiumEventPageGroupPrefix>;

export const tealiumEventPlayerGroupPrefix = 'player_';
export type TealiumEventPlayerGroup = TealiumEventGroup<ITealiumEvent, typeof tealiumEventPlayerGroupPrefix>;

export const tealiumEventExperimentGroupPrefix = 'experiment_';
export type TealiumEventExperimentGroup = TealiumEventGroup<ITealiumEvent, typeof tealiumEventExperimentGroupPrefix>;

// Creating mirror types with camel case props for the Event group constructors
type SnakeToCamel<S extends string> = S extends `${infer T}_${infer U}`
  ? `${Lowercase<T>}${Capitalize<SnakeToCamel<U>>}`
  : S;

type TealiumEventGroupCamelCaseProps<T> = {
  [K in keyof T as SnakeToCamel<string & K>]: T[K];
};

// CC stands for the camel case notation in the prop keys
export type TealiumEventBaseGroupCC = TealiumEventGroupCamelCaseProps<TealiumEventBaseGroup>;
export type TealiumEventParentGroupCC = TealiumEventGroupCamelCaseProps<TealiumEventParentGroup>;
export type TealiumEventMediaGroupCC = TealiumEventGroupCamelCaseProps<TealiumEventMediaGroup>;
export type TealiumEventPageGroupCC = TealiumEventGroupCamelCaseProps<TealiumEventPageGroup>;
export type TealiumEventPlayerGroupCC = TealiumEventGroupCamelCaseProps<TealiumEventPlayerGroup>;
export type TealiumEventExperimentGroupCC = TealiumEventGroupCamelCaseProps<TealiumEventExperimentGroup>;

// Shared helper types and interfaces
export interface IGetEventGroupProps {
  // Returns ether event group or entire event object
  getEventGroupProps():
    | TealiumEventBaseGroup
    | TealiumEventParentGroup
    | TealiumEventMediaGroup
    | TealiumEventPageGroup
    | TealiumEventPlayerGroup
    | TealiumEventExperimentGroup;
}

export interface ITealiumEventConstructorProps {
  eventName: TealiumEventBaseGroupCC['eventName'];
  eventCategory: TealiumEventBaseGroupCC['eventCategory'];
  eventLabel?: TealiumEventBaseGroupCC['eventLabel'];
  eventValue?: TealiumEventBaseGroupCC['eventValue'];
  eventPageSection?: TealiumEventBaseGroupCC['eventPageSection'];
  media?: TealiumEventMediaGroupCC;
  page?: TealiumEventPageGroupCC;
  player?: TealiumEventPlayerGroupCC;
  experiment?: TealiumEventExperimentGroupCC;
  mediaMetadata?: ActivePodcastOrStation;
}
